import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import { inputVariants } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { electronApis } from '@/features/electron-api';
import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';
import { IntegrationIcon } from '@/integrations/integration-icon';

import { Callout } from './callout';
import { DialogHeader } from './dialog-header';
import { FieldLabel } from './field-label';
import { ModalCard } from './modal-card';
import { SetupSteps } from './setup-steps';
import { XeroScopeChips } from './xero-scope-chips';
import {
  describeXeroProblem,
  XERO_DEFAULT_SCOPES,
  XERO_DEVELOPER_URL,
  xeroOrganisationName,
  type XeroCredentials,
  type XeroProblem,
} from './xero-setup';

import type {
  TestedConnectForm,
  TestedConnectProblem,
} from './use-tested-connect';

type XeroConnectForm = TestedConnectForm<XeroCredentials, XeroProblem>;

const INPUT_CLASS = inputVariants({ inputSize: 'sm' });

const SETUP_STEPS = [
  {
    title: 'Create a Custom Connection',
    detail: 'Choose Custom Connection, not Web app.',
  },
  {
    title: 'Turn on these read-only scopes',
    detail: (
      <div className="pt-1">
        <XeroScopeChips scopes={XERO_DEFAULT_SCOPES} />
      </div>
    ),
  },
  {
    title: 'Authorise it for your organisation',
    detail: 'Then copy the client ID and client secret into the fields below.',
  },
];

function problemCopy(problem: TestedConnectProblem<XeroProblem>) {
  if (problem === 'save_failed') {
    return 'Xero connected, but Littlebird couldn’t finish saving it. Try again.';
  }
  if (problem === 'already_connected') return 'Xero is already connected.';
  return describeXeroProblem(problem, { inSetup: true });
}

export const XeroConnectModal = ({
  catalogEntry,
  form,
  onCancel,
}: {
  catalogEntry: OnDeviceMcpCatalogEntry;
  form: XeroConnectForm;
  onCancel: () => void;
}): React.ReactElement => {
  const { connection, changeConnection } = form;
  const organisationName = xeroOrganisationName(form.testResult);

  return (
    <ModalCard onClose={onCancel} closeDisabled={!form.canCancel}>
      <DialogHeader
        icon={<IntegrationIcon iconKey="xero" className="size-5 rounded" />}
        title="Connect Xero"
        subtitle={<span className="block">{catalogEntry.description}</span>}
      />
      <YStack className="gap-5 p-6">
        <SetupSteps
          label="In Xero Developer"
          link={
            <button
              type="button"
              className="text-xs text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
              onClick={() =>
                void electronApis.system.openExternal(XERO_DEVELOPER_URL)
              }
            >
              developer.xero.com
            </button>
          }
          steps={SETUP_STEPS}
        />

        <YStack className="gap-2">
          <FieldLabel>Client ID</FieldLabel>
          <input
            aria-label="Client ID"
            value={connection.clientId}
            disabled={form.busy}
            onChange={(event) =>
              changeConnection({ clientId: event.target.value })
            }
            autoComplete="off"
            spellCheck={false}
            className={INPUT_CLASS}
          />
        </YStack>

        <YStack className="gap-2">
          <FieldLabel>Client secret</FieldLabel>
          <XStack className="gap-2">
            <input
              type="password"
              aria-label="Client secret"
              value={connection.clientSecret}
              disabled={form.busy}
              onChange={(event) =>
                changeConnection({ clientSecret: event.target.value })
              }
              autoComplete="off"
              spellCheck={false}
              className={INPUT_CLASS}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8 shrink-0"
              disabled={!form.canTest}
              isLoading={form.status === 'testing'}
              loadingText="Testing…"
              onClick={() => void form.test()}
            >
              Test
            </Button>
          </XStack>
        </YStack>

        <Collapsible>
          <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground">
            <Icons.chevronRight className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
            Advanced: scopes
          </CollapsibleTrigger>
          <CollapsibleContent>
            <YStack className="gap-1.5 pt-3">
              <FieldLabel>Scopes</FieldLabel>
              <input
                aria-label="Scopes"
                value={connection.scopes}
                disabled={form.busy}
                onChange={(event) =>
                  changeConnection({ scopes: event.target.value })
                }
                spellCheck={false}
                className={`${INPUT_CLASS} font-mono`}
              />
              <span className="text-xs text-foreground-muted">
                Must match the scopes turned on for the Custom Connection.
              </span>
            </YStack>
          </CollapsibleContent>
        </Collapsible>

        {form.problem ? (
          <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
            {problemCopy(form.problem)}
          </Callout>
        ) : form.status === 'passed' ? (
          <XStack className="items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5">
            <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success-3 text-success-11">
              <Icons.check className="size-3.5" />
            </span>
            <span className="text-sm text-foreground">
              Connected to {organisationName ?? 'Xero'}
            </span>
          </XStack>
        ) : null}
      </YStack>
      <XStack className="items-center justify-end gap-2 border-t border-border p-4 px-6">
        <Button
          variant="ghost"
          size="sm"
          disabled={!form.canCancel}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          disabled={!form.canConnect}
          isLoading={form.status === 'saving'}
          loadingText="Connecting…"
          onClick={() => void form.connect()}
        >
          Connect
        </Button>
      </XStack>
    </ModalCard>
  );
};
